import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useRole } from "../../components/AppShell";
import { BackIcon, PlusIcon, WhatsAppIcon } from "../../components/icons";
import { Avatar, ListSkeleton, useConfirm, useToast } from "../../components/ui";
import { ApiError, api } from "../../lib/api";
import { formatPhone, shortTime } from "../../lib/format";
import {
  useAddCaseNote,
  useCase,
  useCaseStatuses,
  useContact,
  useDeleteCase,
  useMe,
  useTeam,
  useUpdateCase,
} from "../../lib/queries";
import type { CaseCard, CaseEvent, CaseStatus } from "../../lib/types";
import { type CaseDraft, CaseFields } from "./CaseFields";
import { caseNumber, describeCaseEvent, fillNotice, KIND_LABEL, lastStatusAt, partRequestNumber } from "./case-text";
import { PartRequestDialog, PartRequestLinks } from "./PartRequestDialog";

export function CasePage() {
  const { caseId } = useParams({ from: "/app/postventa/$caseId" });
  const c = useCase(caseId);
  if (c.isError)
    return (
      <div className="page">
        <BackLink />
        <p className="muted">Este caso ya no existe.</p>
      </div>
    );
  if (!c.data)
    return (
      <div className="page">
        <BackLink />
        <div className="card">
          <ListSkeleton rows={3} />
        </div>
      </div>
    );
  return <CaseView key={c.data.id} c={c.data} />;
}

function BackLink() {
  return (
    <Link className="back-link" to="/postventa">
      <BackIcon />
      Postventa
    </Link>
  );
}

function CaseView({ c }: { c: CaseCard }) {
  const role = useRole();
  const me = useMe().data;
  const statuses = useCaseStatuses().data ?? [];
  const team = useTeam().data?.items ?? [];
  const names = new Map(team.map((m) => [m.id, m.name]));
  const update = useUpdateCase(c.id);
  const remove = useDeleteCase();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  // The message the new status suggests, until it is sent or dismissed.
  const [notice, setNotice] = useState<string | null>(null);

  async function changeStatus(statusId: string) {
    try {
      const saved = await update.mutateAsync({ statusId });
      const text = statuses.find((s) => s.id === statusId)?.notice;
      setNotice(text ? fillNotice(text, saved) : null);
    } catch {
      toast("No se pudo cambiar el estado.");
    }
  }

  async function changeAssignee(assigneeId: string) {
    try {
      await update.mutateAsync({ assigneeId: assigneeId || null });
    } catch (err) {
      toast(
        err instanceof ApiError && err.status === 403 ? "Solo puedes tomar el caso para ti." : "No se pudo asignar.",
      );
    }
  }

  async function onDelete() {
    const ok = await confirm({
      title: `¿Eliminar el caso ${caseNumber(c.number)}?`,
      message:
        "Se borra con su historial y no se puede deshacer. Para terminarlo, mejor pásalo a un estado que lo cierre.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    await remove.mutateAsync(c.id);
    toast("Caso eliminado");
    navigate({ to: "/postventa" });
  }

  // An agent may take a case or leave it; owners and admins hand it to anyone.
  const manager = role === "owner" || role === "admin";
  const assignable = manager ? team : team.filter((m) => m.id === me?.user.id || m.id === c.assigneeId);

  return (
    <div className="page">
      <BackLink />
      <div className="card case-hero">
        <div className="case-hero-main">
          <span className="case-number mono">
            {caseNumber(c.number)}
            {c.reference && <span className="faint"> · {c.reference}</span>}
          </span>
          <h1>{c.title}</h1>
          <span className="contact-hero-tags">
            <span className={`pill ${c.status.closed ? "pill-closed" : "pill-human"}`}>{c.status.label}</span>
            <span className="tag">{KIND_LABEL[c.kind]}</span>
            {(c.product?.name ?? c.model) && <span className="tag">{c.product?.name ?? c.model}</span>}
            <span className="faint">Abierto {shortTime(c.createdAt)}</span>
          </span>
        </div>
        <Link className="case-customer" to="/contactos/$contactId" params={{ contactId: c.contact.id }}>
          <Avatar name={c.contact.name} fallback={c.contact.waId} />
          <span className="entity-main">
            <span className="entity-name">{c.contact.name ?? "Sin nombre"}</span>
            <span className="entity-meta mono">{formatPhone(c.contact.waId)}</span>
          </span>
        </Link>
        <div className="case-controls">
          <div className="field">
            <label htmlFor="case-status">Estado</label>
            <select
              className="select"
              id="case-status"
              value={c.statusId}
              disabled={update.isPending}
              onChange={(e) => changeStatus(e.target.value)}
            >
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.closed ? `${s.label} (cierra)` : s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="case-assignee">Responsable</label>
            <select
              className="select"
              id="case-assignee"
              value={c.assigneeId ?? ""}
              disabled={update.isPending}
              onChange={(e) => changeAssignee(e.target.value)}
            >
              <option value="">Sin responsable</option>
              {assignable.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id === me?.user.id ? `${m.name} (yo)` : m.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {notice !== null && (
        <NoticeCard
          contactId={c.contactId}
          conversationId={c.conversationId}
          text={notice}
          onChange={setNotice}
          onDone={() => setNotice(null)}
        />
      )}

      <ArrivedCard c={c} statuses={statuses} />

      <div className="contact-layout">
        <div className="case-side">
          <DetailsCard c={c} />
          <CasePartsCard c={c} />
        </div>
        <CaseHistory c={c} names={names} />
      </div>

      {manager && (
        <button className="btn btn-danger case-delete" type="button" onClick={onDelete}>
          Eliminar caso
        </button>
      )}
    </div>
  );
}

/**
 * The message the status suggests: the advisor edits it, sends it to the customer's
 * WhatsApp, or skips it. Nothing is sent on its own. Used by cases and part requests.
 */
export function NoticeCard({
  contactId,
  conversationId: from,
  text,
  onChange,
  onDone,
}: {
  contactId: string;
  /** Where it was opened; else the customer's latest conversation. */
  conversationId: string | null;
  text: string;
  onChange: (t: string) => void;
  onDone: () => void;
}) {
  const contact = useContact(contactId).data;
  const conversationId = from ?? contact?.conversations[0]?.id ?? null;
  const toast = useToast();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    if (!conversationId) return;
    setSending(true);
    setError(null);
    try {
      await api.post(`/conversations/${conversationId}/messages`, { body: text.trim() });
      toast("Aviso enviado por WhatsApp");
      onDone();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "window_closed"
          ? "Pasaron más de 24 horas desde el último mensaje del cliente: WhatsApp solo deja escribirle con una plantilla aprobada. Ábrela desde la conversación."
          : "No se pudo enviar el aviso.",
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="card case-notice" aria-labelledby="case-notice-title">
      <div className="card-body">
        <div>
          <h3 id="case-notice-title">¿Avisar al cliente?</h3>
          <p className="muted">Mensaje sugerido para este estado. Revísalo antes de enviarlo.</p>
        </div>
        <textarea
          className="textarea"
          rows={3}
          aria-label="Mensaje para el cliente"
          placeholder="Ej.: ¡Buenas noticias! El caso C-0042 ya está listo para retirar."
          maxLength={4000}
          value={text}
          onChange={(e) => onChange(e.target.value)}
        />
        {!conversationId && contact && (
          <div className="banner banner-warn">Este cliente no tiene conversaciones todavía.</div>
        )}
        {error && (
          <div className="banner banner-warn">
            {error}{" "}
            {conversationId && (
              <Link to="/bandeja" search={{ c: conversationId, f: "all" }}>
                Abrir conversación
              </Link>
            )}
          </div>
        )}
        <div className="row">
          <button className="btn" type="button" onClick={onDone}>
            No avisar
          </button>
          <button
            className="btn btn-primary"
            type="button"
            disabled={sending || !conversationId || !text.trim()}
            onClick={send}
          >
            <WhatsAppIcon />
            {sending ? "Enviando…" : "Enviar por WhatsApp"}
          </button>
        </div>
      </div>
    </section>
  );
}

const draftOf = (c: CaseCard): CaseDraft => ({
  kind: c.kind,
  title: c.title,
  description: c.description,
  productId: c.productId,
  model: c.model,
  serial: c.serial,
  purchaseDate: c.purchaseDate,
  purchasePlace: c.purchasePlace,
  invoice: c.invoice,
  reference: c.reference,
});

function DetailsCard({ c }: { c: CaseCard }) {
  const update = useUpdateCase(c.id);
  const toast = useToast();
  const [d, setD] = useState<CaseDraft>(() => draftOf(c));
  const dirty = JSON.stringify(d) !== JSON.stringify(draftOf(c));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await update.mutateAsync({ ...d, title: d.title.trim() });
      toast("Caso guardado");
    } catch {
      toast("No se pudo guardar.");
    }
  }

  return (
    <form className="card case-form" onSubmit={onSubmit}>
      <div className="card-body">
        <h3>Datos del caso</h3>
        <CaseFields value={d} onChange={setD} idPrefix="case" />
        {c.conversationId && (
          <Link className="conv-link-inline" to="/bandeja" search={{ c: c.conversationId, f: "all" }}>
            <WhatsAppIcon />
            Ver la conversación donde se abrió
          </Link>
        )}
        <div className="row">
          <button className="btn" type="button" disabled={!dirty} onClick={() => setD(draftOf(c))}>
            Descartar
          </button>
          <button className="btn btn-primary" type="submit" disabled={!dirty || update.isPending || !d.title.trim()}>
            {update.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </form>
  );
}

function CaseHistory({ c, names }: { c: CaseCard; names: Map<string, string> }) {
  const add = useAddCaseNote(c.id);
  return <HistoryCard events={c.events} names={names} onAdd={add.mutateAsync} pending={add.isPending} what="case" />;
}

/** The history and internal notes of a case or a part request. */
export function HistoryCard({
  events,
  names,
  onAdd,
  pending,
  what,
}: {
  events: CaseEvent[];
  names: Map<string, string>;
  onAdd: (note: string) => Promise<unknown>;
  pending: boolean;
  what: "case" | "part";
}) {
  const toast = useToast();
  const [note, setNote] = useState("");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await onAdd(note.trim());
      setNote("");
    } catch {
      toast("No se pudo guardar la nota.");
    }
  }

  return (
    <section className="card" aria-labelledby="case-history-title">
      <div className="card-body">
        <h3 id="case-history-title">Historial</h3>
        <form className="case-note-form" onSubmit={onSubmit}>
          <textarea
            className="textarea"
            rows={2}
            aria-label="Nota interna"
            placeholder="Nota interna (el cliente no la ve). Ej.: Llega el repuesto el jueves."
            maxLength={4000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <button className="btn btn-sm" type="submit" disabled={pending || !note.trim()}>
            Agregar nota
          </button>
        </form>
        <ol className="case-timeline">
          {[...events].reverse().map((e) => (
            <li key={e.id} className={e.type === "note" ? "is-note" : undefined}>
              <span className="case-timeline-line">
                {describeCaseEvent(e, names, what)}
                <span className="faint"> · {shortTime(e.createdAt)}</span>
              </span>
              {e.type === "note" && e.text && <p className="case-note">{e.text}</p>}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/**
 * A part the case waits for has arrived: offers moving the case to its next status
 * ("Esperando repuesto" → "En reparación"). Nothing moves on its own.
 */
function ArrivedCard({ c, statuses }: { c: CaseCard; statuses: CaseStatus[] }) {
  const update = useUpdateCase(c.id);
  const toast = useToast();
  const [dismissed, setDismissed] = useState<string | null>(null);
  // A part that arrived after the case's last move: once the case moves, it is not offered again.
  const moved = lastStatusAt(c.events);
  const arrived = c.partRequests.find((r) => r.arrived && Date.parse(r.statusAt) >= moved);
  const at = statuses.findIndex((s) => s.id === c.statusId);
  const next = at >= 0 ? statuses[at + 1] : undefined;
  // Arriving parts move a repair along; closing the case stays the advisor's call.
  if (!arrived || c.status.closed || !next || next.closed || dismissed === `${arrived.id}:${c.statusId}`) return null;
  const target = next;

  async function move() {
    try {
      await update.mutateAsync({ statusId: target.id });
      toast(`Caso pasado a ${target.label}`);
    } catch {
      toast("No se pudo cambiar el estado.");
    }
  }

  return (
    <section className="card case-notice" aria-labelledby="case-arrived-title">
      <div className="card-body">
        <div>
          <h3 id="case-arrived-title">Llegó el repuesto</h3>
          <p className="muted">
            {arrived.partName} ({partRequestNumber(arrived.number)}) está en {arrived.statusLabel}. El caso sigue en{" "}
            {c.status.label}: ¿lo pasas a {target.label}?
          </p>
        </div>
        <div className="row">
          <button className="btn" type="button" onClick={() => setDismissed(`${arrived.id}:${c.statusId}`)}>
            Ahora no
          </button>
          <button className="btn btn-primary" type="button" disabled={update.isPending} onClick={move}>
            Pasar a {target.label}
          </button>
        </div>
      </div>
    </section>
  );
}

/** The parts the case waits for, and the button to ask for one. */
function CasePartsCard({ c }: { c: CaseCard }) {
  const [asking, setAsking] = useState(false);
  return (
    <section className="card" aria-labelledby="case-parts-title">
      <div className="card-body">
        <h3 id="case-parts-title">Repuestos</h3>
        {c.partRequests.length > 0 ? (
          <PartRequestLinks items={c.partRequests} />
        ) : (
          <p className="muted">Si la reparación necesita una pieza, pídela aquí y el caso la sigue.</p>
        )}
        <button className="btn btn-sm case-parts-add" type="button" onClick={() => setAsking(true)}>
          <PlusIcon />
          Pedir repuesto
        </button>
      </div>
      {asking && (
        <PartRequestDialog
          contact={c.contact}
          conversationId={c.conversationId ?? undefined}
          caseId={c.id}
          productId={c.productId}
          model={c.model}
          onClose={() => setAsking(false)}
        />
      )}
    </section>
  );
}
